import { preventOneWordInLastLine } from '@lemonade-hq/cdk';
import { forwardRef } from 'react';
import type { ComponentProps, ElementType, ForwardedRef, PropsWithChildren } from 'react';
import type { Simplify } from 'type-fest';
import { Layout } from '../Layout/Layout';
import type { LayoutSprinklesProps } from '../Layout/Layout.css';
import type { TextVariants } from './Text.css';

const DEFAULT_ELEMENT = 'span';

const componentTypeMap = {
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  bodyMedium: 'p',
  bodySmall: 'p',
  bodyTiny: 'p',
  label: 'label',
} as const satisfies Record<keyof NonNullable<TextVariants>['type'], keyof JSX.IntrinsicElements>;

export type SupportedIntrinsicElements =
  | (typeof componentTypeMap)[keyof typeof componentTypeMap]
  | typeof DEFAULT_ELEMENT;

export type TextProps<TElementType extends SupportedIntrinsicElements> = Simplify<
  LayoutSprinklesProps &
    Omit<JSX.IntrinsicElements[TElementType], keyof TextVariants> &
    TextVariants & { readonly as?: TElementType; readonly htmlFor?: string }
>;

export type TextType = ComponentProps<typeof BaseText>['type'];

const getComponentType = (type: ComponentProps<typeof BaseText>['type']): SupportedIntrinsicElements => {
  if (type === undefined || !(type in componentTypeMap)) return DEFAULT_ELEMENT;

  return componentTypeMap[type];
};

export const BaseText = forwardRef(
  <TElementType extends SupportedIntrinsicElements>(
    {
      children,
      as,
      type,
      textTransform: _textTransform,
      textAlign: _textAlign,
      className,
      ...props
    }: PropsWithChildren<TextProps<TElementType>>,
    ref: ForwardedRef<JSX.IntrinsicElements[TElementType]>,
  ): JSX.Element => {
    const asComponent = (as ?? getComponentType(type)) as ElementType;

    return (
      <Layout as={asComponent} className={className} {...(props as Record<string, unknown>)} ref={ref}>
        {typeof children === 'string' ? preventOneWordInLastLine(children) : children}
      </Layout>
    );
  },
) as <TElementType extends SupportedIntrinsicElements>(
  props: PropsWithChildren<TextProps<TElementType>> & {
    readonly ref?: ForwardedRef<JSX.IntrinsicElements[TElementType]>;
  },
) => JSX.Element;
